import { useEffect, useRef } from 'react';

import { type PageHeaderControl, usePageHeader } from 'components/layout/page/header/context';
import type { CallToActionProps } from 'components/layout/page/CallToAction';

// Serialize only the display-affecting fields of a CTA (label text, disabled, variant, target, and nested action labels/targets).
// Event handlers are deliberately excluded so their per-render identity churn never changes the signature — that would
// otherwise force a needless re-register every render.
const serialiseCta = (cta?: CallToActionProps): string => {

    if (!cta) return '';

    const label = typeof cta.label === 'string' ? cta.label : '';
    const actions = (cta.actions ?? []).map(action => `${typeof action.label === 'string' ? action.label : ''}:${action.to ?? ''}`).join(',');

    return `${label}|${cta.disabled ? 1 : 0}|${cta.variant ?? ''}|${cta.to ?? ''}|${actions}`;

};

const getControlSignature = (control?: PageHeaderControl | null): string => {

    if (!control) return '';

    const actions = (control.actions ?? []).map(serialiseCta).join(';');

    return `${control.exportHandle ? 1 : 0}#${serialiseCta(control.cta)}#${actions}`;

};

/**
 * Dial a control (export control and/or call-to-action) into the nearest PageWithHeader's PageHeader. 
 * No-op when there is no PageWithHeader ancestor, letting callers render an inline fallback instead.
 *
 * The control object may be rebuilt on every render; a stable proxy is registered once and its getters always read 
 * the latest values, so current handlers/labels/disabled state are available at header render time without
 * re-registering on every parent render. The header is refreshed only when a display-affecting field actually changes.
 */
export const useRegisterHeaderControls = (control?: PageHeaderControl | null) => {

    const pageHeader = usePageHeader();

    const idRef = useRef<symbol | null>(null);
    if (idRef.current === null) idRef.current = Symbol('header-control');

    const latestRef = useRef(control);
    latestRef.current = control;

    const proxyRef = useRef<PageHeaderControl | null>(null);
    if (proxyRef.current === null) {

        proxyRef.current = {
            get actions() {

                return latestRef.current?.actions;

            },
            get cta() {

                return latestRef.current?.cta;

            },
            get exportHandle() {

                return latestRef.current?.exportHandle;

            }
        };

    }

    const hasControl = !!control && (!!control.exportHandle || !!control.cta || !!control.actions?.length);
    const signature = getControlSignature(control);

    // Lifecycle: claim the header slot while mounted (and contributing), release it on unmount.
    // Race-safe: unregister only clears the slot if this hook still owns it (see PageWithHeader).
    useEffect(() => {

        if (!pageHeader || !hasControl) return;

        const id = idRef.current as symbol;
        pageHeader.register(id, proxyRef.current as PageHeaderControl);

        return () => pageHeader.unregister(id);

    }, [pageHeader, hasControl]);

    // Refresh: re-register (proxy ref is stable) whenever a display-affecting field changes, so the header re-renders 
    // and reads the latest getter values.
    useEffect(() => {

        if (!pageHeader || !hasControl) return;

        pageHeader.register(idRef.current as symbol, proxyRef.current as PageHeaderControl);

    }, [pageHeader, hasControl, signature]);

};
